import {
  action,
  computed,
  makeObservable,
  observable,
  ObservableMap,
  onBecomeObserved,
  runInAction,
} from "mobx";

import type { AppSubStoreArgs } from "@/store/types";
import { AppSubStore } from "@/store/types";
import type {
  OutlookCalendarEntry,
  OutlookCalendarEvent,
  OutlookCalendarSettings,
} from "@/domains/calendar/outlook/types";
import type {
  OutlookCalendarEntrySchema,
  OutlookCalendarSettingsResponse,
  OutlookCalendarAllSettingsResponse,
  ConnectedCalendarEventRecord,
  ICalendarOutlookStore,
} from "@/store/calendar/types";
import { api } from "@/modules/api";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";
import { windowModule } from "@/modules/window";
import { localStorageModule } from "@/modules/local-storage";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent, CalendarConnectErrorType } from "@/domains/metrics/tracked-event";
import { getCalendarConnectSource } from "@/domains/calendar/getCalendarConnectSource";

export class AppStoreCalendarOutlookStore extends AppSubStore implements ICalendarOutlookStore {
  accounts: ObservableMap<string, OutlookCalendarSettings> = new ObservableMap<
    string,
    OutlookCalendarSettings
  >();
  upcomingMeetings: OutlookCalendarEvent[] = [];

  settingsLoadError = false;

  private ready = false;
  private loading = false;
  private connecting = false;
  private disconnecting = false;
  private updatingCalendars = false;

  constructor(args: AppSubStoreArgs) {
    super(args);

    makeObservable<
      AppStoreCalendarOutlookStore,
      | "ready"
      | "loading"
      | "connecting"
      | "disconnecting"
      | "updatingCalendars"
      | "mapSettingsFromResponse"
      | "setUpcomingMeetingsFromEvents"
    >(this, {
      accounts: observable,
      accountsList: computed,
      upcomingMeetings: observable,
      settingsLoadError: observable,
      ready: observable,
      loading: observable,
      connecting: observable,
      disconnecting: observable,
      updatingCalendars: observable,
      isReady: computed,
      isLoading: computed,
      isConnecting: computed,
      isDisconnecting: computed,
      isUpdatingCalendars: computed,
      isFetchingUpcomingMeetings: computed,
      isConnected: computed,
      loadSettings: action,
      connect: action,
      completeConnection: action,
      disconnect: action,
      updateCalendars: action,
      toggleCalendar: action,
      refreshUpcomingMeetings: action,
      mapSettingsFromResponse: false,
      setUpcomingMeetingsFromEvents: action,
      resetState: action,
    });

    onBecomeObserved(this, "isConnected", () => {
      if (!this.isReady) void this.loadSettings();
    });
  }

  get isReady() {
    return this.ready;
  }

  get isLoading() {
    return this.loading;
  }

  get isConnecting() {
    return this.connecting;
  }

  get isDisconnecting() {
    return this.disconnecting;
  }

  get isUpdatingCalendars() {
    return this.updatingCalendars;
  }

  get isFetchingUpcomingMeetings() {
    return this.store.calendar.isFetchingUpcomingMeetings;
  }

  get accountsList() {
    return Array.from(this.accounts.values());
  }

  get isConnected() {
    return this.accountsList.some(account => account.isConnected);
  }

  async loadSettings() {
    if (this.loading) return;

    runInAction(() => {
      this.loading = true;
    });

    try {
      const response = await api.get("/v2/integrations/calendar/outlook/settings", {});
      const data = response.data;
      if (!data) {
        throw new Error("Missing Outlook Calendar settings payload.");
      }

      // Load all accounts into the store
      const accounts = (data as OutlookCalendarAllSettingsResponse).accounts ?? {};
      runInAction(() => {
        this.accounts = new ObservableMap<string, OutlookCalendarSettings>();
        for (const [subjectId, accountSettings] of Object.entries(accounts)) {
          if (accountSettings) {
            this.accounts.set(subjectId, this.mapSettingsFromResponse(accountSettings));
          }
        }
      });

      if (this.accounts.size > 0) {
        await this.store.calendar.fetchUpcomingMeetingsFromStore();
      }
      runInAction(() => {
        this.ready = true;
        this.settingsLoadError = false;
      });
    } catch (unknownError) {
      runInAction(() => {
        this.ready = true;
        this.settingsLoadError = true;
      });
      logger.error({
        message: "[AppStoreCalendarOutlookStore] Failed to load settings",
        info: { error: objectModule.safeErrorAsJson(unknownError as Error) },
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async refreshUpcomingMeetings() {
    await this.store.calendar.refreshUpcomingMeetings();
  }

  async connect() {
    if (this.connecting) return;

    runInAction(() => {
      this.connecting = true;
    });

    trackEvent(TrackedEvent.CalendarConnectStart, {
      provider: "outlook",
      source: getCalendarConnectSource(),
    });

    try {
      const redirectUri = windowModule.getOutlookCalendarOAuthRedirectUrl();
      const response = await api.get("/v2/integrations/calendar/outlook/connect", {
        params: {
          query: {
            redirect_uri: redirectUri,
          },
        },
      });

      const data = response.data;
      if (!data) {
        throw new Error("Missing Outlook Calendar connect payload.");
      }

      localStorageModule.writeOutlookCalendarOAuthState(data.state);
      await windowModule.navigateToExternal({
        url: new URL(data.authorization_url),
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreCalendarOutlookStore] Failed to start Outlook Calendar connect flow",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      trackEvent(TrackedEvent.CalendarConnectFailed, {
        provider: "outlook",
        error_type: "api_error" as CalendarConnectErrorType,
        error_message: error.message,
        source: getCalendarConnectSource(),
      });

      toastModule.triggerToast({
        content: "Failed to start Outlook Calendar connection. Please try again.",
      });
    } finally {
      runInAction(() => {
        this.connecting = false;
      });
    }
  }

  async completeConnection({
    code,
    state,
    redirectUri,
  }: {
    code: string;
    state: string | null;
    redirectUri: string;
  }) {
    const storedState = localStorageModule.readOutlookCalendarOAuthState();
    if (!storedState || storedState !== state) {
      throw new Error("OAuth state mismatch.");
    }

    try {
      const response = await api.post("/v2/integrations/calendar/outlook/oauth/callback", {
        body: {
          code,
          redirect_uri: redirectUri,
          state,
        },
      });

      const data = response.data;
      if (!data) {
        throw new Error("Missing Outlook Calendar OAuth callback payload.");
      }

      const subjectId = data.outlook_subject_id;
      if (!subjectId) {
        throw new Error("Missing outlook_subject_id in Outlook Calendar OAuth callback response.");
      }

      runInAction(() => {
        this.accounts.set(subjectId, this.mapSettingsFromResponse(data));
      });
      await this.refreshUpcomingMeetings();
      toastModule.triggerToast({
        content: "Outlook Calendar connected",
      });

      return { subjectId };
    } catch (unknownError) {
      logger.error({
        message:
          "[AppStoreCalendarOutlookStore] Failed to complete Outlook Calendar OAuth callback",
        info: { error: objectModule.safeErrorAsJson(unknownError as Error) },
      });
      throw unknownError;
    } finally {
      localStorageModule.clearOutlookCalendarOAuthState();
    }
  }

  async disconnect(subjectId: string) {
    if (this.disconnecting) return;
    if (!subjectId) {
      logger.warn({
        message: "[AppStoreCalendarOutlookStore] Cannot disconnect: no subject_id",
      });
      return;
    }

    runInAction(() => {
      this.disconnecting = true;
    });

    trackEvent(TrackedEvent.CalendarDisconnectStart, {
      provider: "outlook",
      calendar_connection_id: subjectId,
    });

    try {
      const response = await api.post("/v2/integrations/calendar/outlook/disconnect", {
        params: {
          query: {
            subject_id: subjectId,
          },
        },
      });
      const data = response.data;
      if (!data) {
        throw new Error("Missing Outlook Calendar disconnect payload.");
      }

      // Update or remove the account from the store
      if (data.is_connected) {
        runInAction(() => {
          this.accounts.set(subjectId, this.mapSettingsFromResponse(data));
        });
      } else {
        runInAction(() => {
          this.accounts.delete(subjectId);
        });
      }

      // Clear upcoming meetings if no accounts are connected
      if (!this.isConnected) {
        runInAction(() => {
          this.upcomingMeetings = [];
        });
      }

      toastModule.triggerToast({
        content: "Outlook Calendar disconnected",
      });

      trackEvent(TrackedEvent.CalendarDisconnectComplete, {
        provider: "outlook",
        calendar_connection_id: subjectId,
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreCalendarOutlookStore] Failed to disconnect Outlook Calendar",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: "Failed to disconnect Outlook Calendar. Please try again.",
      });
    } finally {
      runInAction(() => {
        this.disconnecting = false;
      });
    }
  }

  async updateCalendars(subjectId: string, calendars: OutlookCalendarEntry[]) {
    if (this.updatingCalendars) return;

    const account = this.accounts.get(subjectId);
    if (!account) {
      throw new Error(`Cannot update calendars: account with subject_id ${subjectId} not found`);
    }

    const previousCalendars = account.calendars.map(calendar => ({ ...calendar }));

    runInAction(() => {
      this.updatingCalendars = true;
    });

    try {
      const enabledCalendarIds = calendars
        .filter(calendar => calendar.isEnabled)
        .map(calendar => calendar.id);
      const response = await api.post("/v2/integrations/calendar/outlook/settings", {
        body: {
          enabled_calendar_ids: enabledCalendarIds,
          subject_id: subjectId,
        },
      });

      const data = response.data;
      if (!data) {
        throw new Error("Missing Outlook Calendar update settings payload.");
      }

      runInAction(() => {
        this.accounts.set(subjectId, this.mapSettingsFromResponse(data));
      });

      if (this.isConnected) {
        await this.store.calendar.fetchUpcomingMeetingsFromStore();
      } else {
        runInAction(() => {
          this.upcomingMeetings = [];
        });
      }
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreCalendarOutlookStore] Failed to update calendar settings",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      runInAction(() => {
        const account = this.accounts.get(subjectId);
        if (account) {
          this.accounts.set(subjectId, {
            ...account,
            calendars: previousCalendars,
          });
        }
      });

      toastModule.triggerToast({
        content: "Failed to update calendars. Please try again.",
      });
    } finally {
      runInAction(() => {
        this.updatingCalendars = false;
      });
    }
  }

  async toggleCalendar({
    subjectId,
    calendarId,
    isEnabled,
  }: {
    subjectId: string;
    calendarId: string;
    isEnabled: boolean;
  }) {
    const account = this.accounts.get(subjectId);
    if (!account) return;

    trackEvent(TrackedEvent.CalendarToggle, {
      provider: "outlook",
      connected_calendar_id: calendarId,
      calendar_connection_id: subjectId,
      enabled: isEnabled,
    });

    const updatedCalendars = account.calendars.map(calendar =>
      calendar.id === calendarId ? { ...calendar, isEnabled } : calendar
    );

    await this.updateCalendars(subjectId, updatedCalendars);
  }

  resetState() {
    this.accounts.clear();
    this.upcomingMeetings = [];
    this.ready = false;
    this.loading = false;
    this.connecting = false;
    this.disconnecting = false;
    this.updatingCalendars = false;
    this.settingsLoadError = false;
  }

  private mapSettingsFromResponse(
    response: OutlookCalendarSettingsResponse
  ): OutlookCalendarSettings {
    return {
      isConnected: response.is_connected ?? false,
      calendars: (response.calendars ?? []).map(mapCalendarEntryFromResponse),
      outlookAccountEmail: response.outlook_account_email ?? null,
      outlookSubjectId: response.outlook_subject_id ?? null,
      connectedAt: response.connected_at ?? null,
      revokedAt: response.revoked_at ?? null,
      lastSyncedAt: response.last_synced_at ?? null,
    };
  }

  setUpcomingMeetingsFromEvents(events: ConnectedCalendarEventRecord[]) {
    if (!events || events.length === 0) {
      this.upcomingMeetings = [];
      return;
    }

    this.upcomingMeetings = events.map(event => {
      const value = event.value ?? {};
      const summary = typeof value.summary === "string" ? value.summary : null;
      const htmlLink = typeof value.html_link === "string" ? value.html_link : null;
      const teamsLink = typeof value.teams_link === "string" ? value.teams_link : null;
      const location = typeof value.location === "string" ? value.location : null;
      return {
        calendarId: event.connected_calendar_id,
        eventId: event.external_event_id,
        summary,
        startAt: event.start_at,
        endAt: event.end_at,
        htmlLink,
        teamsLink,
        location,
      };
    });
  }
}

export const mapCalendarEntryFromResponse = (
  entry: OutlookCalendarEntrySchema
): OutlookCalendarEntry => {
  return {
    id: entry.id,
    isPrimary: entry.is_default ?? false,
    summary: entry.name ?? null,
    timeZone: null,
    accessRole: null,
    isEnabled: Boolean(entry.config?.is_enabled),
    backgroundColor: entry.background_color ?? undefined,
  };
};
